import {
  clearCreateAdDraft,
  getCreateAdDraft,
  setCreateAdDraft,
} from 'api/utils/createAdDraftStorage'
import classNames from 'classnames'
import ConfirmDialog from 'components/common/ConfirmDialog'
import NativeDateInput from 'components/common/field/NativeDateInput'
import CreateAdAddressSection from 'components/createAd/CreateAdAddressSection'
import {
  isCreateAdAddressStepValid,
  hasGeocodedAddress,
  isValidCity,
  isValidNeighborhood,
  isValidStreet,
  isValidUnit,
} from 'components/createAd/createAdAddressValidation'
import {
  AMENITY_KEYS,
  buildRegisterPayload,
  CreateAdFlowForm,
  initialCreateAdFlowForm,
  isValidCanadianPostalCode,
  LaundryOption,
  ListingType,
  normalizePetsForForm,
  ParkingType,
  RoomCount,
  SERVICE_KEYS,
  TRANSFER_REASON_KEYS,
  TransferReason,
} from 'components/createAd/createAdFlowState'
import CreateAdPhotoUpload from 'components/createAd/CreateAdPhotoUpload'
import CreateAdPublicationReview, {
  type PublicationPublishChecks,
} from 'components/createAd/CreateAdPublicationReview'
import CreateAdPublishSuccess from 'components/createAd/CreateAdPublishSuccess'
import {
  areDescriptionDatesValid,
  capTransferIncentive,
  digitsOnlyInteger,
  ELECTROMENAGERS_PAID_NOTE_MAX,
  isLowMonthlyRentWarning,
  isValidAreaSqFt,
  isValidMonthlyRent,
  isValidTransferIncentive,
  MONTHLY_RENT_MAX,
  maxSelectableDate,
  PARKING_EXTRA_NOTE_MAX,
  PETS_CONDITIONS_MAX,
  todayIsoDate,
  validateAvailabilityDate,
  validateContractEndDate,
  validateDesiredMoveInDate,
} from 'components/createAd/createAdValidation'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { moneyFormatLocale, normalizeLangCode } from 'utils/i18nLocale'
import {
  digitsOnlyMoney,
  formatMoneyDisplay,
  MONTHLY_RENT_MAX_DOLLARS,
  parseMoneyAmount,
  sanitizeMoneyInput,
} from 'utils/moneyInput'
import { scrollAppToTop } from 'utils/scrollAppToTop'

const FORM_STEP_COUNT = 5
const SUCCESS_STEP = 5
const LISTING_TITLE_MAX = 80
const LISTING_DESCRIPTION_MAX = 1200
const LISTING_SUPPLEMENTARY_MAX = 300

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const fieldInputClass =
  'w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 focus:outline-none focus:ring-0'

function CharacterCounter({
  current,
  max,
  className,
}: {
  current: number
  max: number
  className?: string
}) {
  return (
    <span className={classNames('text-xs tabular-nums text-gray-400', className)}>
      {current} / {max}
    </span>
  )
}


function DateField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  errorMessage,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  errorMessage?: string
}) {
  const labelId = `${id}-label`

  return (
    <div>
      <span id={labelId} className="mb-1.5 block text-sm font-semibold text-gray-800">
        {label}
      </span>
      <div className="relative">
        <NativeDateInput
          id={id}
          aria-labelledby={labelId}
          aria-invalid={Boolean(errorMessage)}
          value={value}
          min={min}
          max={max}
          onChange={e => onChange(e.target.value)}
          className={classNames(
            fieldInputClass,
            'date-input-native cursor-text',
            errorMessage ? 'border-red-500' : undefined
          )}
        />
      </div>
      {errorMessage ? (
        <p role="alert" className="mt-1.5 text-xs leading-snug text-red-600">
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}

function ToggleRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-gray-100 px-4 py-3">
      <span className="min-w-0 flex-1 text-sm font-medium text-gray-800">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={classNames(
          'relative h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-0',
          checked ? 'bg-imovPrimary' : 'bg-gray-300'
        )}
      >
        <span
          className={classNames(
            'absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  )
}

function SectionCard({
  icon,
  title,
  children,
  className,
}: {
  icon: ReactNode
  title: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={classNames(
        'rounded-2xl border border-gray-100 bg-white p-6 shadow-sm',
        className
      )}
    >
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
          {icon}
        </div>
        <h2 className="text-lg font-semibold text-imovSecondary">{title}</h2>
      </div>
      {children}
    </section>
  )
}

const PencilIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
)

const DocumentIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
)

const InfoIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const CalendarIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
)

const CloudIcon = () => (
  <svg className="h-5 w-5 text-imovPrimary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 15a4.5 4.5 0 004.5 4.5h7.5a3.75 3.75 0 001.332-7.257 3 3 0 00-3.536-3.536 6.768 6.768 0 00-1.29-.153 4.5 4.5 0 00-8.046 3.684z"
    />
  </svg>
)

function StepperIcon({ stepIndex, variant }: { stepIndex: number; variant: 'active' | 'upcoming' }) {
  if (stepIndex === 0) {
    if (variant === 'upcoming') {
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    }
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" />
      </svg>
    )
  }
  if (stepIndex === 1) {
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={variant === 'upcoming' ? 1.75 : 2.25}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    )
  }
  if (stepIndex === 2) {
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={variant === 'upcoming' ? 1.75 : 2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )
  }
  if (stepIndex === 3) {
    return (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={variant === 'upcoming' ? 1.75 : 2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  }
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={variant === 'upcoming' ? 1.75 : 2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.856 2.649m-.001-5.784a14.923 14.923 0 01-5.856-2.648M12 20v-8" />
    </svg>
  )
}

export type CreateAdSubmitResult = {
  listingId: string
  mode: 'created' | 'updated'
}

interface CreateAdRegistrationFlowProps {
  isAuthenticated: boolean
  accountEmail?: string
  isSubmitting: boolean
  onSubmit: (payload: Record<string, unknown>) => Promise<CreateAdSubmitResult | void>
  onCancel: () => void
  initialForm?: CreateAdFlowForm | null
  initialSuccess?: CreateAdSubmitResult | null
  /** Persist in-progress draft across refresh (disabled when editing an existing listing). */
  enableDraftPersistence?: boolean
}

const CreateAdRegistrationFlow = ({
  isAuthenticated: _isAuthenticated,
  accountEmail = '',
  isSubmitting,
  onSubmit,
  onCancel,
  initialForm,
  initialSuccess,
  enableDraftPersistence = true,
}: CreateAdRegistrationFlowProps) => {
  const { t, i18n } = useTranslation()
  const moneyLocale = moneyFormatLocale(normalizeLangCode(i18n.language))
  const savedDraft = enableDraftPersistence ? getCreateAdDraft(FORM_STEP_COUNT - 1) : null
  const [step, setStep] = useState(() =>
    initialForm ? 0 : (savedDraft?.step ?? 0)
  )
  const [form, setForm] = useState<CreateAdFlowForm>(() => {
    const base = initialForm ?? savedDraft?.form ?? initialCreateAdFlowForm()
    return {
      ...base,
      pets: normalizePetsForForm(base.pets),
      electromenagersMode:
        base.electromenagersMode ||
        (base.services?.electromenagers ? 'included' : ''),
      services: { ...base.services, electromenagers: false },
    }
  })
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  /** Last step validated via Suivant (blocks forward jumps to unvisited steps). */
  const [highestCompletedStep, setHighestCompletedStep] = useState(() =>
    initialForm ? FORM_STEP_COUNT - 1 : (savedDraft?.highestCompletedStep ?? -1)
  )
  /** Highest step index ever opened — allows returning after navigating back. */
  const [furthestStepReached, setFurthestStepReached] = useState(() =>
    initialForm
      ? FORM_STEP_COUNT - 1
      : (savedDraft?.furthestStepReached ?? savedDraft?.step ?? 0)
  )
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [expertTipIndex, setExpertTipIndex] = useState(0)
  const [postalFieldBlurred, setPostalFieldBlurred] = useState(false)
  const [streetFieldBlurred, setStreetFieldBlurred] = useState(false)
  const [unitFieldBlurred, setUnitFieldBlurred] = useState(false)
  const [cityFieldBlurred, setCityFieldBlurred] = useState(false)
  const [neighborhoodFieldBlurred, setNeighborhoodFieldBlurred] = useState(false)
  const [addressStepAttempted, setAddressStepAttempted] = useState(false)
  const [monthlyRentBlurred, setMonthlyRentBlurred] = useState(false)
  const [areaFieldBlurred, setAreaFieldBlurred] = useState(false)
  const hasAppliedInitialFormRef = useRef(Boolean(initialForm))
  const [publishChecks, setPublishChecks] = useState<PublicationPublishChecks>({
    accurate: false,
    visible: false,
    terms: false,
  })
  const [publishChecksError, setPublishChecksError] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [publishSuccess, setPublishSuccess] = useState<CreateAdSubmitResult | null>(
    initialSuccess ?? null
  )

  useEffect(() => {
    if (!initialForm || hasAppliedInitialFormRef.current) return
    setForm(initialForm)
    setStep(0)
    setHighestCompletedStep(FORM_STEP_COUNT - 1)
    setFurthestStepReached(FORM_STEP_COUNT - 1)
    hasAppliedInitialFormRef.current = true
  }, [initialForm])

  useEffect(() => {
    if (step === SUCCESS_STEP) return
    setFurthestStepReached(prev => Math.max(prev, step))
  }, [step])

  useEffect(() => {
    if (!initialSuccess) return
    clearCreateAdDraft()
    setPublishSuccess(initialSuccess)
    setStep(SUCCESS_STEP)
  }, [initialSuccess])

  useLayoutEffect(() => {
    scrollAppToTop()
  }, [step])

  useEffect(() => {
    if (!enableDraftPersistence || step === SUCCESS_STEP) return

    setCreateAdDraft({
      form,
      step,
      highestCompletedStep,
      furthestStepReached,
    })
  }, [enableDraftPersistence, form, step, highestCompletedStep, furthestStepReached])

  const expertTips = useMemo(
    () => [
      t('createAd.flow.sidebar.expertTips.tip1Quote'),
      t('createAd.flow.sidebar.expertTips.tip2Quote'),
      t('createAd.flow.sidebar.expertTips.tip3Quote'),
    ],
    [t]
  )

  useEffect(() => {
    const id = setInterval(() => {
      setExpertTipIndex(i => (i + 1) % expertTips.length)
    }, 6000)
    return () => clearInterval(id)
  }, [expertTips.length])

  const touchSaved = useCallback(() => {
    setSaveStatus('saving')
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saved')
    }, 400)
  }, [])

  const update = useCallback(
    <K extends keyof CreateAdFlowForm>(key: K, value: CreateAdFlowForm[K]) => {
      setForm(prev => ({ ...prev, [key]: value }))
      touchSaved()
    },
    [touchSaved]
  )

  const updatePets = useCallback(
    (value: 'yes' | 'no') => {
      setForm(prev => ({
        ...prev,
        pets: value,
        petsConditionsNote: value === 'no' ? '' : prev.petsConditionsNote,
      }))
      touchSaved()
    },
    [touchSaved]
  )

  const isEditingListing = Boolean(initialForm)

  const handlePhotosChange = useCallback(
    (photos: CreateAdFlowForm['listingPhotos']) => {
      setForm(prev => ({
        ...prev,
        listingPhotos: photos,
        photosRightsConfirmed:
          isEditingListing && photos.length > 0 ? true : prev.photosRightsConfirmed,
      }))
      touchSaved()
    },
    [isEditingListing, touchSaved]
  )

  const postalCompact = form.postalCode.trim().replace(/\s/g, '')
  const postalInvalid =
    postalCompact.length > 0 && !isValidCanadianPostalCode(form.postalCode)
  const showPostalError =
    postalInvalid && (postalCompact.length >= 5 || postalFieldBlurred || addressStepAttempted)

  const showStreetError =
    (streetFieldBlurred || addressStepAttempted) && !isValidStreet(form.street)

  const showUnitError =
    (unitFieldBlurred || addressStepAttempted) &&
    form.unit.trim() !== '' &&
    !isValidUnit(form.unit)

  const showCityError =
    (cityFieldBlurred || addressStepAttempted) &&
    (form.city.trim() === '' || !isValidCity(form.city))

  const showNeighborhoodError =
    (neighborhoodFieldBlurred || addressStepAttempted) &&
    form.neighborhood.trim() !== '' &&
    !isValidNeighborhood(form.neighborhood)

  const showGeocodeError =
    addressStepAttempted &&
    isValidStreet(form.street) &&
    isValidCity(form.city) &&
    isValidCanadianPostalCode(form.postalCode) &&
    !hasGeocodedAddress(form)

  const monthlyRentAmount = parseMoneyAmount(form.monthlyRent)
  const showMonthlyRentError =
    (monthlyRentBlurred || addressStepAttempted) && !isValidMonthlyRent(form.monthlyRent)

  const showMonthlyRentMaxError =
    (monthlyRentBlurred || addressStepAttempted) &&
    monthlyRentAmount !== null &&
    monthlyRentAmount > MONTHLY_RENT_MAX

  const showMonthlyRentWarning =
    monthlyRentBlurred &&
    isValidMonthlyRent(form.monthlyRent) &&
    isLowMonthlyRentWarning(form.monthlyRent)

  const showAreaError =
    areaFieldBlurred && form.areaSqFt.trim() !== '' && !isValidAreaSqFt(form.areaSqFt)

  const dateMin = todayIsoDate()
  const dateMax = maxSelectableDate()

  const desiredMoveInErrorKey = validateDesiredMoveInDate(form.desiredMoveInDate)
  const availabilityErrorKey = validateAvailabilityDate(form.availabilityDate)
  const contractEndErrorKey = validateContractEndDate(
    form.contractEndDate,
    form.availabilityDate,
    form.desiredMoveInDate
  )

  const dateErrorMessage = (key: ReturnType<typeof validateDesiredMoveInDate>) => {
    if (!key) return undefined
    if (key === 'desiredMoveInDate') return t('createAd.flow.validation.desiredMoveInPast')
    if (key === 'availabilityDate') return t('createAd.flow.validation.availabilityPast')
    if (contractEndErrorKey === 'contractEndDate') {
      const trimmed = form.contractEndDate.trim()
      if (trimmed && trimmed < dateMin) {
        return t('createAd.flow.validation.contractEndPast')
      }
      return t('createAd.flow.validation.contractEndBeforeOthers')
    }
    return undefined
  }

  const stepValid = useMemo(() => {
    if (step === 0) {
      return (
        !!form.listingType &&
        !!form.unitType.trim() &&
        isCreateAdAddressStepValid(form) &&
        isValidMonthlyRent(form.monthlyRent)
      )
    }
    if (step === 1) {
      return isValidAreaSqFt(form.areaSqFt)
    }
    if (step === 2) {
      if (form.listingPhotos.length === 0) return false
      if (isEditingListing) return true
      return form.photosRightsConfirmed
    }
    if (step === 3) {
      return (
        form.desiredMoveInDate.trim() !== '' &&
        form.contractEndDate.trim() !== '' &&
        form.availabilityDate.trim() !== '' &&
        areDescriptionDatesValid(form) &&
        isValidTransferIncentive(form.transferIncentive)
      )
    }
    if (step === 4) {
      return true
    }
    return true
  }, [step, form, isEditingListing])

  const handleCancel = () => setCancelDialogOpen(true)

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false)
    if (enableDraftPersistence) {
      clearCreateAdDraft()
    }
    onCancel()
  }

  const handleNext = () => {
    if (step === 0) {
      setAddressStepAttempted(true)
      setStreetFieldBlurred(true)
      setUnitFieldBlurred(true)
      setCityFieldBlurred(true)
      setNeighborhoodFieldBlurred(true)
      setPostalFieldBlurred(true)
      setMonthlyRentBlurred(true)
    }
    if (!stepValid) return
    setHighestCompletedStep(prev => Math.max(prev, step))
    setStep(s => Math.min(s + 1, FORM_STEP_COUNT - 1))
  }

  const handlePrev = () => setStep(s => Math.max(s - 1, 0))

  const handleStepClick = (targetStep: number) => {
    if (targetStep === step) return
    // Forward only to steps already reached; backward always within visited range.
    if (targetStep > furthestStepReached) return
    if (targetStep > step && targetStep > highestCompletedStep) return
    setStep(targetStep)
  }

  const publishInFlightRef = useRef(false)

  const handlePublish = async () => {
    const checksOk =
      publishChecks.accurate && publishChecks.visible && publishChecks.terms
    if (!checksOk) {
      setPublishChecksError(true)
      toast.error(String(t('createAd.flow.publication.preview.publishChecksError')))
      return
    }
    if (isSubmitting || publishInFlightRef.current) return
    publishInFlightRef.current = true
    setPublishChecksError(false)
    try {
      const payload = buildRegisterPayload(form, accountEmail.trim() || undefined)
      const result = await onSubmit(payload)
      if (result?.listingId) {
        if (enableDraftPersistence) {
          clearCreateAdDraft()
        }
        setPublishSuccess(result)
        setStep(SUCCESS_STEP)
      }
    } finally {
      publishInFlightRef.current = false
    }
  }

  const isSuccessStep = step === SUCCESS_STEP

  const roomOptions: { value: RoomCount; labelKey: string }[] = [
    { value: 'studio', labelKey: 'createAd.flow.rooms.studio' },
    { value: '2.5', labelKey: 'createAd.flow.rooms.2_5' },
    { value: '3.5', labelKey: 'createAd.flow.rooms.3_5' },
    { value: '4.5', labelKey: 'createAd.flow.rooms.4_5' },
    { value: '5.5+', labelKey: 'createAd.flow.rooms.5_5p' },
  ]

  const bathroomOptions: { value: string; labelKey: string }[] = [
    { value: '1', labelKey: 'createAd.flow.bathroomChoices.1' },
    { value: '2', labelKey: 'createAd.flow.bathroomChoices.2' },
    { value: '3', labelKey: 'createAd.flow.bathroomChoices.3' },
    { value: '4', labelKey: 'createAd.flow.bathroomChoices.4' },
    { value: '5+', labelKey: 'createAd.flow.bathroomChoices.5p' },
  ]

  const laundryOptions: { value: LaundryOption; labelKey: string }[] = [
    { value: 'in_unit', labelKey: 'createAd.flow.laundry.inUnit' },
    { value: 'in_building', labelKey: 'createAd.flow.laundry.inBuilding' },
    { value: 'none', labelKey: 'createAd.flow.laundry.none' },
  ]

  const parkingTypes: { value: ParkingType; labelKey: string }[] = [
    { value: 'none', labelKey: 'createAd.flow.parking.none' },
    { value: 'outdoor', labelKey: 'createAd.flow.parking.outdoor' },
    { value: 'indoor', labelKey: 'createAd.flow.parking.indoor' },
    { value: 'garage', labelKey: 'createAd.flow.parking.garage' },
  ]

  const petsOptions: { value: 'yes' | 'no'; labelKey: string }[] = [
    { value: 'yes', labelKey: 'createAd.flow.pets.yes' },
    { value: 'no', labelKey: 'createAd.flow.pets.no' },
  ]

  const stepMeta = [
    {
      title: t('createAd.flow.hero.informationsTitle'),
      subtitle: t('createAd.flow.hero.informationsSubtitle'),
    },
    {
      title: t('createAd.flow.hero.detailsTitle'),
      subtitle: t('createAd.flow.hero.detailsSubtitle'),
    },
    {
      title: t('createAd.flow.hero.photosTitle'),
      subtitle: t('createAd.flow.hero.photosSubtitle'),
    },
    {
      title: t('createAd.flow.hero.descriptionTitle'),
      subtitle: t('createAd.flow.hero.descriptionSubtitle'),
    },
    {
      title: t('createAd.flow.hero.publicationTitle'),
      subtitle: t('createAd.flow.hero.publicationSubtitle'),
    },
  ]

  const stepLabels = [
    t('createAd.flow.steps.informations'),
    t('createAd.flow.steps.details'),
    t('createAd.flow.steps.photos'),
    t('createAd.flow.steps.description'),
    isSuccessStep
      ? t('createAd.flow.steps.awaitingValidation')
      : t('createAd.flow.steps.publication'),
  ]

  const renderStepper = () => (
    <nav
      className="mb-12 w-full"
      aria-label={t('createAd.flow.progressLabel')}
    >
      <div className="mx-auto grid w-full max-w-4xl grid-cols-5 gap-0">
        {stepLabels.map((label, i) => {
          const done =
            isSuccessStep || i < step || (i <= highestCompletedStep && step > i)
          const active = !isSuccessStep && i === step
          const clickable =
            !isSuccessStep && i <= furthestStepReached && i !== step
          return (
            <div key={label} className="flex min-w-0 flex-col items-center">
              <div className="flex w-full items-center">
                <div
                  className={classNames(
                    'h-0.5 min-h-[2px] flex-1 rounded-full transition-colors',
                    i === 0 ? 'opacity-0' : step > i - 1 ? 'bg-imovPrimary' : 'bg-gray-200'
                  )}
                  aria-hidden
                />
                <button
                  type="button"
                  disabled={!clickable}
                  aria-current={active ? 'step' : undefined}
                  aria-label={label}
                  onClick={() => handleStepClick(i)}
                  className={classNames(
                    'relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-imovPrimary/30',
                    done && 'bg-green-500 text-white shadow-sm',
                    active && 'bg-imovPrimary text-white shadow-lg shadow-imovPrimary/30 ring-4 ring-imovPrimary/15',
                    !done && !active && 'bg-gray-100 text-gray-400',
                    clickable && 'cursor-pointer hover:opacity-90'
                  )}
                >
                  {done ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <StepperIcon stepIndex={i} variant={active ? 'active' : 'upcoming'} />
                  )}
                </button>
                <div
                  className={classNames(
                    'h-0.5 min-h-[2px] flex-1 rounded-full transition-colors',
                    i === FORM_STEP_COUNT - 1 ? 'opacity-0' : isSuccessStep || step > i ? 'bg-imovPrimary' : 'bg-gray-200'
                  )}
                  aria-hidden
                />
              </div>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => handleStepClick(i)}
                className={classNames(
                  'mt-3 max-w-[5.5rem] text-center text-xs font-semibold leading-tight sm:max-w-none sm:text-sm',
                  active && 'text-imovPrimary',
                  done && !active && 'text-green-600',
                  !done && !active && 'text-gray-400',
                  clickable && 'cursor-pointer hover:underline'
                )}
              >
                {label}
              </button>
            </div>
          )
        })}
      </div>
    </nav>
  )

  const listingTypeOptions: { value: ListingType; labelKey: string }[] = [
    { value: 'lease_transfer', labelKey: 'createAd.flow.listingTypes.lease_transfer' },
    { value: 'sublease', labelKey: 'createAd.flow.listingTypes.sublease' },
    { value: 'for_rent', labelKey: 'createAd.flow.listingTypes.for_rent' },
  ]

  const unitTypeOptions: { value: string; labelKey: string }[] = [
    { value: 'apartment', labelKey: 'createAd.flow.unitTypes.apartment' },
    { value: 'condo', labelKey: 'createAd.flow.unitTypes.condo' },
    { value: 'house', labelKey: 'createAd.flow.unitTypes.house' },
    { value: 'loft', labelKey: 'createAd.flow.unitTypes.loft' },
    { value: 'other', labelKey: 'createAd.flow.unitTypes.other' },
  ]

  const renderInformations = () => (
    <div className="grid gap-8 lg:grid-cols-[1fr_minmax(260px,320px)] lg:items-start">
      <div className="space-y-6">
        <SectionCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          }
          title={t('createAd.flow.sections.housingType')}
        >
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 md:gap-8">
            <div className="min-w-0">
              <label className="mb-1.5 block text-sm font-semibold text-imovSecondary">
                {t('createAd.flow.fields.listingType')}
              </label>
              <div className="relative">
                <select
                  value={form.listingType}
                  onChange={e => update('listingType', e.target.value as ListingType)}
                  className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-900 focus:outline-none focus:ring-0"
                >
                  <option value="">{t('createAd.flow.placeholders.select')}</option>
                  {listingTypeOptions.map(({ value, labelKey }) => (
                    <option key={value} value={value}>
                      {t(labelKey)}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <label className="mb-1.5 block text-sm font-semibold text-imovSecondary">
                {t('createAd.flow.fields.unitType')}
              </label>
              <div className="relative">
                <select
                  value={form.unitType}
                  onChange={e => update('unitType', e.target.value)}
                  className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-900 focus:outline-none focus:ring-0"
                >
                  <option value="">{t('createAd.flow.placeholders.select')}</option>
                  {unitTypeOptions.map(({ value, labelKey }) => (
                    <option key={value} value={value}>
                      {t(labelKey)}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          title={t('createAd.flow.sections.address')}
        >
          <CreateAdAddressSection
            form={form}
            update={update}
            showPostalError={showPostalError}
            onPostalBlur={() => setPostalFieldBlurred(true)}
            showStreetError={showStreetError}
            showUnitError={showUnitError}
            showCityError={showCityError}
            showNeighborhoodError={showNeighborhoodError}
            showGeocodeError={showGeocodeError}
            onStreetBlur={() => setStreetFieldBlurred(true)}
            onUnitBlur={() => setUnitFieldBlurred(true)}
            onCityBlur={() => setCityFieldBlurred(true)}
            onNeighborhoodBlur={() => setNeighborhoodFieldBlurred(true)}
          />
        </SectionCard>

        <SectionCard
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          title={t('createAd.flow.sections.monthlyRent')}
        >
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-800">{t('createAd.flow.fields.currentRent')}</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                $
              </span>
              <input
                inputMode="decimal"
                value={formatMoneyDisplay(form.monthlyRent, moneyLocale)}
                onChange={e =>
                  update(
                    'monthlyRent',
                    sanitizeMoneyInput(e.target.value, MONTHLY_RENT_MAX_DOLLARS)
                  )
                }
                onFocus={() => setMonthlyRentBlurred(false)}
                onBlur={() => setMonthlyRentBlurred(true)}
                aria-invalid={showMonthlyRentError || showMonthlyRentMaxError}
                className={classNames(
                  'w-full rounded-xl border py-3 pl-8 pr-28 focus:outline-none focus:ring-0',
                  showMonthlyRentError || showMonthlyRentMaxError
                    ? 'border-red-500'
                    : 'border-gray-200'
                )}
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                {t('createAd.flow.suffix.cadPerMonth')}
              </span>
            </div>
            {showMonthlyRentError ? (
              <p role="alert" className="mt-1.5 text-xs leading-snug text-red-600">
                {t('createAd.flow.validation.monthlyRentRequired')}
              </p>
            ) : null}
            {showMonthlyRentMaxError ? (
              <p role="alert" className="mt-1.5 text-xs leading-snug text-red-600">
                {t('createAd.flow.validation.monthlyRentMax', {
                  max: MONTHLY_RENT_MAX.toLocaleString(moneyLocale),
                })}
              </p>
            ) : null}
            {showMonthlyRentWarning ? (
              <p className="mt-1.5 text-xs leading-snug text-amber-700">
                {t('createAd.flow.validation.monthlyRentLowWarning')}
              </p>
            ) : null}
          </div>
        </SectionCard>
      </div>

      <aside className="space-y-4 lg:sticky lg:top-24">
        <div className="rounded-2xl border border-imovPrimary/15 bg-imovPrimary/5 p-5">
          <div className="mb-3 flex items-center gap-2 text-imovPrimary">
            <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="font-semibold">{t('createAd.flow.sidebar.whyTitle')}</h3>
          </div>
          <p className="text-sm leading-relaxed text-gray-600">{t('createAd.flow.sidebar.whyBody')}</p>
          <ul className="mt-4 space-y-2 text-sm text-gray-700">
            <li className="flex gap-2">
              <span className="text-imovPrimary">✓</span>
              {t('createAd.flow.sidebar.whyBullet1')}
            </li>
            <li className="flex gap-2">
              <span className="text-imovPrimary">✓</span>
              {t('createAd.flow.sidebar.whyBullet2')}
            </li>
          </ul>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-imovPrimary">
              {t('createAd.flow.sidebar.expertLabel')}
            </span>
            <div className="flex gap-1">
              {expertTips.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={t('createAd.flow.sidebar.expertDot', { index: i + 1 })}
                  onClick={() => setExpertTipIndex(i)}
                  className={classNames(
                    'h-1.5 w-1.5 rounded-full transition-colors',
                    i === expertTipIndex ? 'bg-imovPrimary' : 'bg-gray-300'
                  )}
                />
              ))}
            </div>
          </div>
          <p className="text-sm italic leading-relaxed text-gray-700">&ldquo;{expertTips[expertTipIndex]}&rdquo;</p>
          <p className="mt-3 text-xs text-gray-500">{t('createAd.flow.sidebar.expertFoot')}</p>
        </div>

        <p className="flex items-start gap-2 text-xs text-gray-500">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          {t('createAd.flow.sidebar.privacy')}
        </p>
      </aside>
    </div>
  )

  const toggleService = (key: string) => {
    if (key === 'electromenagers') return
    setForm(prev => ({
      ...prev,
      services: { ...prev.services, [key]: !prev.services[key] },
    }))
    touchSaved()
  }

  const updateElectromenagersMode = useCallback(
    (value: 'included' | 'paid' | 'no') => {
      setForm(prev => ({
        ...prev,
        electromenagersMode: value,
        electromenagersPaidNote: value === 'paid' ? prev.electromenagersPaidNote : '',
        services: { ...prev.services, electromenagers: false },
      }))
      touchSaved()
    },
    [touchSaved]
  )

  const toggleAmenity = (key: string) => {
    setForm(prev => ({
      ...prev,
      amenities: { ...prev.amenities, [key]: !prev.amenities[key] },
    }))
    touchSaved()
  }

  const renderTransferDetailsCard = () => (
    <SectionCard icon={<CalendarIcon />} title={t('createAd.flow.sections.transferDetails')}>
      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id="create-ad-desired-move-in"
          label={t('createAd.flow.fields.desiredMoveInDate')}
          value={form.desiredMoveInDate}
          min={dateMin}
          max={dateMax}
          errorMessage={dateErrorMessage(desiredMoveInErrorKey)}
          onChange={value => update('desiredMoveInDate', value)}
        />
        <DateField
          id="create-ad-availability"
          label={t('createAd.flow.fields.availabilityDate')}
          value={form.availabilityDate}
          min={dateMin}
          max={dateMax}
          errorMessage={dateErrorMessage(availabilityErrorKey)}
          onChange={value => update('availabilityDate', value)}
        />
        <DateField
          id="create-ad-contract-end"
          label={t('createAd.flow.fields.contractEndDate')}
          value={form.contractEndDate}
          min={dateMin}
          errorMessage={dateErrorMessage(contractEndErrorKey)}
          onChange={value => update('contractEndDate', value)}
        />
        <div>
          <label
            className="mb-1.5 block text-sm font-semibold text-gray-800"
            htmlFor="create-ad-transfer-reason"
          >
            {t('createAd.flow.fields.transferReason')}
          </label>
          <div className="relative">
            <select
              id="create-ad-transfer-reason"
              value={form.transferReason}
              onChange={e => update('transferReason', e.target.value as TransferReason | '')}
              className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-900 focus:outline-none focus:ring-0"
            >
              <option value="">{t('createAd.flow.placeholders.select')}</option>
              {TRANSFER_REASON_KEYS.map(key => (
                <option key={key} value={key}>
                  {t(`createAd.flow.transferReasons.${key}`)}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-semibold text-gray-800" htmlFor="create-ad-transfer-incentive">
          {t('createAd.flow.fields.transferIncentive')}
        </label>
        <div className="relative">
          <input
            id="create-ad-transfer-incentive"
            inputMode="numeric"
            value={formatMoneyDisplay(form.transferIncentive, moneyLocale)}
            onChange={e => update('transferIncentive', capTransferIncentive(e.target.value))}
            placeholder="0"
            className={classNames(fieldInputClass, 'pr-10')}
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
            $
          </span>
        </div>
        <p className="mt-1.5 text-xs text-gray-500">{t('createAd.flow.hints.transferIncentive')}</p>
      </div>
    </SectionCard>
  )

  const renderDetails = () => (
    <div className="mx-auto max-w-3xl space-y-8">
      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        }
        title={t('createAd.flow.sections.mainFeatures')}
      >
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-800">{t('createAd.flow.fields.roomCount')}</p>
            <div className="flex flex-wrap gap-2">
              {roomOptions.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => update('roomCount', value)}
                  className={classNames(
                    'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-0',
                    form.roomCount === value
                      ? 'border-imovPrimary bg-imovPrimary/5 text-imovPrimary'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  )}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 sm:items-start">
            <div>
              <p className="mb-2 text-sm font-semibold text-gray-800">{t('createAd.flow.fields.bathrooms')}</p>
              <div className="flex flex-wrap gap-2">
                {bathroomOptions.map(({ value, labelKey }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => update('bathrooms', value)}
                    className={classNames(
                      'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-0',
                      form.bathrooms === value
                        ? 'border-imovPrimary bg-imovPrimary/5 text-imovPrimary'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    )}
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-800">{t('createAd.flow.fields.area')}</label>
              <input
                inputMode="numeric"
                placeholder={t('createAd.flow.placeholders.area')}
                value={form.areaSqFt}
                onChange={e => update('areaSqFt', digitsOnlyInteger(e.target.value))}
                onBlur={() => setAreaFieldBlurred(true)}
                aria-invalid={showAreaError}
                className={classNames(
                  'w-full rounded-xl border py-3 px-4 focus:outline-none focus:ring-0',
                  showAreaError ? 'border-red-500' : 'border-gray-200'
                )}
              />
              {showAreaError ? (
                <p role="alert" className="mt-1.5 text-xs leading-snug text-red-600">
                  {t('createAd.flow.validation.invalidArea')}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        }
        title={t('createAd.flow.sections.services')}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_KEYS.filter(key => key !== 'electromenagers').map(key => {
            const on = form.services[key]
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleService(key)}
                className={classNames(
                  'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-0',
                  on ? 'bg-imovPrimary/10 text-imovPrimary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200/80'
                )}
              >
                <span
                  className={classNames(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                    on ? 'border-imovPrimary bg-white' : 'border-gray-300 bg-white'
                  )}
                  aria-hidden
                >
                  {on ? <span className="h-2 w-2 rounded-full bg-imovPrimary" /> : null}
                </span>
                {t(`createAd.flow.services.${key}`)}
              </button>
            )
          })}
        </div>
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="mb-2 text-sm font-semibold text-gray-800">
            {t('createAd.flow.services.electromenagers')}
          </p>
          <div className="inline-flex w-full max-w-full rounded-xl bg-gray-100 p-1 sm:w-auto">
            {(
              [
                { value: 'included' as const, labelKey: 'createAd.flow.electromenagers.included' },
                { value: 'paid' as const, labelKey: 'createAd.flow.electromenagers.paid' },
                { value: 'no' as const, labelKey: 'createAd.flow.electromenagers.no' },
              ] as const
            ).map(({ value, labelKey }) => (
              <button
                key={value}
                type="button"
                onClick={() => updateElectromenagersMode(value)}
                className={classNames(
                  'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-0 sm:flex-none sm:px-4',
                  form.electromenagersMode === value
                    ? 'bg-imovPrimary text-white shadow-sm'
                    : 'text-gray-600 hover:text-imovSecondary'
                )}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
          {form.electromenagersMode === 'paid' ? (
            <div className="mt-4">
              <label
                htmlFor="electromenagers-paid-note"
                className="mb-1.5 block text-sm font-semibold text-gray-800"
              >
                {t('createAd.flow.fields.electromenagersPaidNote')}
                <span className="ml-1 font-normal text-gray-500">
                  ({t('createAd.flow.hints.optional')})
                </span>
              </label>
              <textarea
                id="electromenagers-paid-note"
                rows={2}
                maxLength={ELECTROMENAGERS_PAID_NOTE_MAX}
                value={form.electromenagersPaidNote}
                onChange={e =>
                  update(
                    'electromenagersPaidNote',
                    e.target.value.slice(0, ELECTROMENAGERS_PAID_NOTE_MAX)
                  )
                }
                placeholder={t('createAd.flow.placeholders.electromenagersPaidNote')}
                className="w-full resize-y rounded-xl border border-gray-200 py-3 px-4 text-sm focus:outline-none focus:ring-0"
              />
              <CharacterCounter
                current={form.electromenagersPaidNote.length}
                max={ELECTROMENAGERS_PAID_NOTE_MAX}
                className="mt-1.5 block text-right"
              />
            </div>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        }
        title={t('createAd.flow.sections.laundry')}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {laundryOptions.map(({ value, labelKey }) => {
            const selected = form.laundry === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => update('laundry', value)}
                className={classNames(
                  'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors focus:outline-none focus:ring-0',
                  selected ? 'bg-imovPrimary/10 text-imovPrimary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200/80'
                )}
              >
                <span
                  className={classNames(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                    selected ? 'border-imovPrimary bg-white' : 'border-gray-300 bg-white'
                  )}
                  aria-hidden
                >
                  {selected ? <span className="h-2 w-2 rounded-full bg-imovPrimary" /> : null}
                </span>
                {t(labelKey)}
              </button>
            )
          })}
        </div>
      </SectionCard>

      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
        title={t('createAd.flow.sections.conditions')}
      >
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <div className="min-w-0 flex-1 space-y-3">
            <p className="mb-2 text-sm font-semibold text-gray-800">
              {t('createAd.flow.fields.conditionToggles')}
            </p>
            <ToggleRow
              label={t('createAd.flow.fields.smoker')}
              checked={form.smokerAllowed}
              onChange={v => update('smokerAllowed', v)}
            />
            <ToggleRow
              label={t('createAd.flow.fields.pmr')}
              checked={form.pmrAccessible}
              onChange={v => update('pmrAccessible', v)}
            />
            <ToggleRow
              label={t('createAd.flow.fields.shortTerm')}
              checked={form.shortTermOk}
              onChange={v => update('shortTermOk', v)}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-2 text-sm font-semibold text-gray-800">{t('createAd.flow.fields.pets')}</p>
            <div className="inline-flex w-full max-w-full rounded-xl bg-gray-100 p-1 sm:w-auto">
              {petsOptions.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updatePets(value)}
                  className={classNames(
                    'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-0 sm:flex-none sm:px-4',
                    (form.pets === value || (value === 'yes' && form.pets === 'conditions'))
                      ? 'bg-imovPrimary text-white shadow-sm'
                      : 'text-gray-600 hover:text-imovSecondary'
                  )}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
            {form.pets === 'yes' || form.pets === 'conditions' ? (
              <div className="mt-4">
                <label
                  htmlFor="pets-conditions-note"
                  className="mb-1.5 block text-sm font-semibold text-gray-800"
                >
                  {t('createAd.flow.fields.petsConditionsNote')}
                  <span className="ml-1 font-normal text-gray-500">
                    ({t('createAd.flow.hints.optional')})
                  </span>
                </label>
                <textarea
                  id="pets-conditions-note"
                  rows={3}
                  maxLength={PETS_CONDITIONS_MAX}
                  value={form.petsConditionsNote}
                  onChange={e =>
                    update(
                      'petsConditionsNote',
                      e.target.value.slice(0, PETS_CONDITIONS_MAX)
                    )
                  }
                  placeholder={t('createAd.flow.placeholders.petsConditions')}
                  className="w-full resize-y rounded-xl border border-gray-200 py-3 px-4 text-sm focus:outline-none focus:ring-0"
                />
                <CharacterCounter
                  current={form.petsConditionsNote.length}
                  max={PETS_CONDITIONS_MAX}
                  className="mt-1.5 block text-right"
                />
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        }
        title={t('createAd.flow.sections.parking')}
      >
        <div className="space-y-4">
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800">{t('createAd.flow.fields.parkingType')}</p>
            <p className="mb-2 text-xs leading-snug text-gray-500">
              {t('createAd.flow.hints.parkingTypeIncluded')}
            </p>
            <div className="flex flex-wrap gap-2">
              {parkingTypes.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    update('parkingType', value)
                    if (value === 'none') {
                      update('parkingPrice', '')
                    }
                  }}
                  className={classNames(
                    'rounded-full border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-0',
                    form.parkingType === value
                      ? 'border-imovPrimary bg-imovPrimary/5 text-imovPrimary'
                      : 'border-gray-200 text-gray-700'
                  )}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div
            className={classNames(
              'grid gap-4',
              form.parkingType && form.parkingType !== 'none'
                ? 'sm:grid-cols-2'
                : 'max-w-xs'
            )}
          >
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-800">{t('createAd.flow.fields.parkingSpaces')}</label>
              <input
                inputMode="numeric"
                placeholder={t('createAd.flow.placeholders.parkingSpaces')}
                value={form.parkingSpaces}
                onChange={e => update('parkingSpaces', e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 px-4 focus:outline-none focus:ring-0"
              />
            </div>
            {form.parkingType && form.parkingType !== 'none' ? (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-800">
                  {t('createAd.flow.fields.parkingPrice')}
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                    $
                  </span>
                  <input
                    inputMode="numeric"
                    placeholder={t('createAd.flow.placeholders.parkingPrice')}
                    value={formatMoneyDisplay(form.parkingPrice, moneyLocale)}
                    onChange={e => update('parkingPrice', digitsOnlyMoney(e.target.value))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-8 pr-28 focus:outline-none focus:ring-0"
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                    {t('createAd.flow.suffix.cadPerMonth')}
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-snug text-gray-500">
                  {t('createAd.flow.hints.parkingPriceIncluded')}
                </p>
              </div>
            ) : null}
          </div>
          <div>
            <label
              htmlFor="parking-extra-note"
              className="mb-1.5 block text-sm font-semibold text-gray-800"
            >
              {t('createAd.flow.fields.parkingExtraNote')}
              <span className="ml-1 font-normal text-gray-500">
                ({t('createAd.flow.hints.optional')})
              </span>
            </label>
            <textarea
              id="parking-extra-note"
              rows={2}
              maxLength={PARKING_EXTRA_NOTE_MAX}
              value={form.parkingExtraNote}
              onChange={e =>
                update('parkingExtraNote', e.target.value.slice(0, PARKING_EXTRA_NOTE_MAX))
              }
              placeholder={t('createAd.flow.placeholders.parkingExtraNote')}
              className="w-full resize-y rounded-xl border border-gray-200 py-3 px-4 text-sm focus:outline-none focus:ring-0"
            />
            <p className="mt-1.5 text-xs leading-snug text-gray-500">
              {t('createAd.flow.hints.parkingExtraNote')}
            </p>
            <CharacterCounter
              current={form.parkingExtraNote.length}
              max={PARKING_EXTRA_NOTE_MAX}
              className="mt-1 block text-right"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(
              [
                ['parkingEv', form.parkingEv, t('createAd.flow.parkingExtras.ev')],
                ['parkingBike', form.parkingBike, t('createAd.flow.parkingExtras.bike')],
                ['parkingLocker', form.parkingLocker, t('createAd.flow.parkingExtras.locker')],
              ] as const
            ).map(([key, checked, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={checked}
                onClick={() => update(key, !checked)}
                className={classNames(
                  'flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-xl border-2 p-3 text-center transition-colors focus:outline-none focus:ring-0',
                  checked
                    ? 'border-imovPrimary bg-imovPrimary/5 text-imovPrimary'
                    : 'border-gray-200 bg-white text-gray-500'
                )}
              >
                <span className="text-[10px] font-bold uppercase leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title={t('createAd.flow.sections.amenities')}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {AMENITY_KEYS.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => toggleAmenity(key)}
              className={classNames(
                'flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-xl border-2 p-3 text-center transition-colors focus:outline-none focus:ring-0',
                form.amenities[key]
                  ? 'border-imovPrimary bg-imovPrimary/5 text-imovPrimary'
                  : 'border-gray-200 text-gray-500'
              )}
            >
              <span className="text-[10px] font-bold uppercase leading-tight">{t(`createAd.flow.amenities.${key}`)}</span>
            </button>
          ))}
        </div>
      </SectionCard>
    </div>
  )

  const renderPhotos = () => (
    <div className="mx-auto max-w-3xl">
      <CreateAdPhotoUpload
        photos={form.listingPhotos ?? []}
        onPhotosChange={handlePhotosChange}
        virtualTourLink={form.virtualTourLink}
        onVirtualTourLinkChange={value => update('virtualTourLink', value)}
        photosRightsConfirmed={form.photosRightsConfirmed}
        onPhotosRightsConfirmedChange={value => update('photosRightsConfirmed', value)}
      />
    </div>
  )

  const renderDescription = () => (
    <div className="mx-auto max-w-3xl space-y-6">
      <SectionCard icon={<PencilIcon />} title={t('createAd.flow.description.titleLabel')}>
        <input
          type="text"
          value={form.listingTitle}
          maxLength={LISTING_TITLE_MAX}
          onChange={e => update('listingTitle', e.target.value.slice(0, LISTING_TITLE_MAX))}
          placeholder={t('createAd.flow.description.titlePlaceholder')}
          className={fieldInputClass}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">{t('createAd.flow.description.titleHint')}</p>
          <CharacterCounter current={form.listingTitle.length} max={LISTING_TITLE_MAX} />
        </div>
      </SectionCard>

      {renderTransferDetailsCard()}

      <SectionCard icon={<DocumentIcon />} title={t('createAd.flow.description.label')}>
        <textarea
          value={form.description}
          maxLength={LISTING_DESCRIPTION_MAX}
          onChange={e =>
            update('description', e.target.value.slice(0, LISTING_DESCRIPTION_MAX))
          }
          rows={10}
          placeholder={t('createAd.flow.description.placeholder')}
          className={classNames(fieldInputClass, 'resize-y min-h-[200px]')}
        />
        <div className="mt-2 flex items-start justify-between gap-3">
          <p className="text-xs text-gray-500">{t('createAd.flow.description.bodyHint')}</p>
          <CharacterCounter
            current={form.description.length}
            max={LISTING_DESCRIPTION_MAX}
            className="shrink-0"
          />
        </div>
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-gray-700">
          <span className="font-semibold text-imovPrimary">{t('createAd.flow.description.tipLabel')}</span>{' '}
          {t('createAd.flow.description.tipText')}
        </div>
      </SectionCard>

      <SectionCard
        icon={<InfoIcon />}
        title={
          <>
            {t('createAd.flow.description.supplementaryLabel')}
            <span className="ml-2 text-sm font-normal text-gray-400">
              {t('createAd.flow.description.optional')}
            </span>
          </>
        }
      >
        <textarea
          value={form.supplementaryInfo}
          maxLength={LISTING_SUPPLEMENTARY_MAX}
          onChange={e =>
            update('supplementaryInfo', e.target.value.slice(0, LISTING_SUPPLEMENTARY_MAX))
          }
          rows={4}
          placeholder={t('createAd.flow.description.supplementaryPlaceholder')}
          className={classNames(fieldInputClass, 'resize-y')}
        />
        <div className="mt-2 flex justify-end">
          <CharacterCounter
            current={form.supplementaryInfo.length}
            max={LISTING_SUPPLEMENTARY_MAX}
          />
        </div>
      </SectionCard>
    </div>
  )

  const renderPublication = () => (
    <div className="space-y-6">
      <CreateAdPublicationReview
        form={form}
        publishChecks={publishChecks}
        onPublishChecksChange={checks => {
          setPublishChecks(checks)
          if (publishChecksError) setPublishChecksError(false)
        }}
        publishChecksError={publishChecksError}
      />
    </div>
  )

  const statusMessage =
    saveStatus === 'saving'
      ? t('createAd.flow.footer.draftSaving')
      : saveStatus === 'error'
        ? t('createAd.flow.footer.draftError')
        : t('createAd.flow.footer.draftSaved')

  return (
    <>
    <ConfirmDialog
      isOpen={cancelDialogOpen}
      title={t('createAd.flow.cancelModal.title')}
      message={t('createAd.flow.cancelConfirm')}
      cancelLabel={t('createAd.flow.cancelModal.stay')}
      confirmLabel={t('createAd.flow.cancelModal.leave')}
      confirmVariant="danger"
      onCancel={() => setCancelDialogOpen(false)}
      onConfirm={handleCancelConfirm}
    />
    <div className={classNames('bg-[#f8f9fa]', isSuccessStep ? 'pb-12' : 'pb-28')}>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-5 text-sm">
          <Link to="/" className="text-gray-500 transition-colors hover:text-imovPrimary">
            {t('createAd.flow.breadcrumb.home')}
          </Link>
          <span className="mx-2 text-gray-300">/</span>
          <span className="font-semibold text-imovSecondary">{t('createAd.flow.breadcrumb.ceder')}</span>
        </div>

        {!isSuccessStep ? (
          <header className="mb-10 text-left">
            <h1 className="text-3xl font-bold tracking-tight text-imovSecondary sm:text-4xl">
              {stepMeta[step].title}
            </h1>
            <p className="mt-2 max-w-2xl text-base text-gray-500">{stepMeta[step].subtitle}</p>
          </header>
        ) : null}

        {renderStepper()}

        {step === 0 && renderInformations()}
        {step === 1 && renderDetails()}
        {step === 2 && renderPhotos()}
        {step === 3 && renderDescription()}
        {step === 4 && renderPublication()}
        {isSuccessStep && publishSuccess ? (
          <CreateAdPublishSuccess
            listingId={publishSuccess.listingId}
            mode={publishSuccess.mode}
          />
        ) : null}
      </div>

      {!isSuccessStep ? (
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CloudIcon />
            <span
              className={classNames(
                saveStatus === 'error' && 'text-red-600',
                saveStatus === 'saving' && 'text-imovPrimary'
              )}
            >
              {statusMessage}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              {t('createAd.flow.footer.cancel')}
            </button>
            {step > 0 && (
              <button
                type="button"
                onClick={handlePrev}
                className="rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                {t('createAd.flow.footer.previous')}
              </button>
            )}
            {step < FORM_STEP_COUNT - 1 ? (
              <button
                type="button"
                disabled={!stepValid}
                onClick={handleNext}
                className={classNames(
                  'inline-flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors',
                  stepValid ? 'bg-imovPrimary hover:bg-imovPrimaryDark' : 'cursor-not-allowed bg-gray-300'
                )}
              >
                {t('createAd.flow.footer.next')}
                <span aria-hidden>→</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handlePublish}
                className={classNames(
                  'inline-flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-sm',
                  isSubmitting
                    ? 'cursor-not-allowed bg-gray-300'
                    : 'bg-imovPrimary hover:bg-imovPrimaryDark'
                )}
              >
                {isSubmitting ? t('createAd.flow.footer.publishing') : t('createAd.flow.footer.publish')}
              </button>
            )}
          </div>
        </div>
      </div>
      ) : null}
    </div>
    </>
  )
}

export default CreateAdRegistrationFlow
